import { useFeed } from "../hooks/useFeed";
import { PanelHeader } from "./PanelHeader";
import type { BusRouteRef, TransitDeparture, TransitSystemId } from "@kmitl/shared";

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

const SYSTEM_COLOR: Record<TransitSystemId, string> = {
  "srt-eastern":   "var(--transit-srt)",
  "arl-cityline":  "var(--transit-arl)",
  "kmitl-shuttle": "var(--transit-bus)",
};

const SYSTEM_LABEL: Record<TransitSystemId, string> = {
  "srt-eastern":   "SRT",
  "arl-cityline":  "ARL",
  "kmitl-shuttle": "BUS",
};

function minuteLabel(m: number): string {
  if (m <= 0) return "NOW";
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h${m % 60 > 0 ? `${m % 60}m` : ""}`;
}

function statusClass(s: TransitDeparture["status"]): string {
  if (s === "delayed")   return "transit-delayed";
  if (s === "cancelled") return "transit-cancelled";
  return "";
}

/** BMTA routes serving campus — static reference (Bible §3 Table 3.3). */
function BusRoutesStrip() {
  const feed = useFeed<BusRouteRef>(`${API_BASE}/api/campus/bus-routes`, 60 * 60_000);
  if (feed.data.length === 0) return null;
  return (
    <div className="bus-routes-strip" aria-label="Bus routes serving campus">
      <p className="mono eyebrow" style={{ color: "var(--ink-low)", margin: "6px 0 3px" }}>
        BMTA ROUTES · {feed.data.length} SERVE CAMPUS
      </p>
      <div className="bus-routes-chips">
        {feed.data.map((r) => (
          <span
            key={r.route}
            className="bus-route-chip mono"
            title={`${r.termini.en} — ${r.stopNote}`}
            style={r.service === "terminates-at-campus" ? { background: "var(--transit-bus)", color: "#fff", borderColor: "var(--transit-bus)" } : undefined}
          >
            {r.route}
          </span>
        ))}
      </div>
    </div>
  );
}

export function TransitBoard() {
  const feed = useFeed<TransitDeparture>(`${API_BASE}/api/campus/transit`, 30_000);
  const rows = feed.data.slice(0, 12);

  return (
    <div className="transit-board left-section">
      <PanelHeader
        title="DEPARTURES — LAT KRABANG"
        ageMinutes={feed.ageMinutes}
        fallbackTier={feed.fallbackTier === "loading" ? undefined : feed.fallbackTier}
        source="srt-arl-kmitl"
      />
      <div className="transit-rows">
        {rows.length === 0 && (
          <p className="transit-empty mono">Loading…</p>
        )}
        {rows.map((d) => {
          const color = SYSTEM_COLOR[d.system];
          const label = SYSTEM_LABEL[d.system];
          return (
            <div key={d.id} className={`transit-row ${statusClass(d.status)}`}>
              <span className="transit-chip mono" style={{ background: color }}>
                {label}
              </span>
              <span className="transit-dest mono">{d.destination.en}</span>
              <span className="transit-platform mono" title={`Platform: ${d.platform}`}>
                {d.platform.replace("Platform ", "P").replace(" Platform", "")}
              </span>
              <span
                className="transit-eta mono"
                style={{ color: d.minutesUntil <= 3 ? "var(--warn)" : "var(--ink)" }}
                title={d.status === "delayed" ? "Delayed" : d.status === "cancelled" ? "Cancelled" : ""}
              >
                {d.status === "cancelled" ? "CNCL" : minuteLabel(d.minutesUntil)}
              </span>
            </div>
          );
        })}
      </div>
      <BusRoutesStrip />
      <p className="transit-note mono">
        Published static tables · no live rail feed exists — confirm at platform
      </p>
    </div>
  );
}
